import React, {useMemo, useState} from 'react';
import {Badge, Button, Card, DatePicker, InputNumber, Switch, Table, Typography} from "antd";
import {useProjectForm} from "../../../providers/ProjectFormProvider";
import InfoTooltip from "../../../components/InfoTooltip";
import PositionCandidatesTable from "./PositionCandidatesTable";
import {CheckOutlined, CloseOutlined, ArrowRightOutlined} from "@ant-design/icons";
import PricingStrategyForm from "./PricingStrategyForm";
import {usePositionForm} from "../../../providers/PositionFormProvider";

const {Text} = Typography;

const PositionDeliverablesTable = ({...props}) => {
    const {
        project,
        onFormSubmit: onProjectFormSubmit,
    } = useProjectForm();

    const {
        formModel: position,
        isProjectUpdating,
        onFormChange,
        onFormReset,
        onFormSubmit,
        generateScoping,
        isLoading,
        isDisabled
    } = usePositionForm();

    const selectedStrategy = position.fixedFeePricingStrategyValue();
    const [isEditingStrategy, setIsEditingStrategy] = useState(false);


    const posDescription = useMemo(() => {
        let description = position.description || '';
        if (TB.stripHtml(description)?.length > 0) {
            return description
        }
        return '<i>No Description Provided</i>'
    }, [position?.description, isProjectUpdating]);

    const totalHours = useMemo(() => {
        if (!position) {
            return 0;
        }
        let defaultTotal = TB.sumBy(TB.filter(position?.deliverables || [], ['is_active', true]), 'total_hours');
        if (position.assigned_candidate) {
            return position.assigned_candidate?.job_candidate?.total_hours || defaultTotal;
        }
        return defaultTotal;
    }, [
        isProjectUpdating,
        isProjectUpdating,
        isDisabled,
        isLoading,
        position?.assigned_candidate?.job_candidate?.total_hours,
        TB.map(position?.deliverables || [], 'is_active'),
    ]);

    const estimatedTalentPayout = useMemo(() => {

        return position.exact_premarkup_cost || position.exact_standard_premarkup_cost
    }, [
        isProjectUpdating,
        isDisabled,
        isLoading,
        position?.assigned_candidate?.job_candidate?.exact_premarkup_cost,
        position?.exact_standard_platform_rate,
        totalHours,
        position.exact_premarkup_cost,
        position.exact_standard_premarkup_rate
    ])
    const estimatedClientCost = useMemo(() => {

        return position.exact_platform_cost || position.exact_standard_platform_cost
    }, [
        isProjectUpdating,
        isDisabled,
        isLoading,
        position?.assigned_candidate?.job_candidate?.exact_platform_cost,
        position?.exact_platform_cost
    ])

    const onDeliverableChange = async (deliverableId, props) => {
        let deliverables = TB.map([...position.deliverables], del => {
            if (del.id === deliverableId) {
                return {
                    ...del,
                    ...props
                }
            }
            return del;
        })

        return onFormChange({...position, deliverables})
    }
    const [editingDeliverable, setEditingDeliverable] = useState(false);

    const [isEditingPayout, setIsEditingPayout] = useState(false);

    return (
        <div className="flex flex-col gap-6 items-stretch px-4 py-8">

            <div className="flex flex-row items-start justify-between">

                <div className="flex flex-col gap-3">
                    <p className="text-base flex flex-row gap-1.5">
                        <span className="font-semibold">
                            Role:
                        </span>
                        <span className="font-semibold text-primary">
                            {position.title}
                        </span>
                    </p>
                    <div className="flex flex-col gap-1.5">
                        <p className="text-base font-semibold">Description: </p>
                        <div className="relative">
                            <div className="text-xs line-clamp-6 overflow-ellipsis"
                                 dangerouslySetInnerHTML={{__html: posDescription}}/>
                        </div>
                    </div>
                    <p className="text-base flex flex-row items-center gap-1.5">
                        <span className="font-semibold">
                            Proposal Pricing Strategy:
                        </span>
                        <span className="font-semibold text-primary">
                            {selectedStrategy ? (
                                <span className="flex flex-row gap-3 items-center">
                                    <Text strong className="text-primary">
                                        {position.getFixedFeePricingStrategyName(selectedStrategy)}
                                    </Text>
                                    {!isEditingStrategy && (
                                        <Button type="primary" onClick={() => setIsEditingStrategy(true)}>
                                            Update
                                        </Button>
                                    )}
                                </span>
                            ) : (
                                <span className="flex flex-row gap-3 items-center">
                                    <Text strong className="text-danger italic">
                                        Not Set
                                    </Text>
                                    {!isEditingStrategy && (
                                        <Button type="primary" onClick={() => setIsEditingStrategy(true)}>
                                            Update
                                        </Button>
                                    )}
                                </span>
                            )}
                        </span>
                    </p>
                    {position.fixedFeePricingStrategyIsTotalPayout() && (
                        <div className="text-base flex flex-row items-center gap-1.5 mt-2 relative">
                            <span className="font-semibold whitespace-nowrap">
                                Target Talent Payout:
                            </span>
                            {isEditingPayout ? (
                                <div className="flex flex-row gap-2 items-center">
                                    <InputNumber
                                        controls={false}
                                        min={1}
                                        addonBefore="$"
                                        precision={2}
                                        size="small"
                                        readOnly={position.hasBeenFilled()}
                                        defaultValue={position.payout_amount || 0}
                                        onChange={value => {

                                            let deliverables = TB.map([...position.deliverables], del => {
                                                return {
                                                    ...del,
                                                    payout_amount: null
                                                }
                                            })
                                            onFormChange({...position, deliverables,payout_amount: value})
                                        }}
                                        disabled={isProjectUpdating}
                                    />
                                    <Button type="default"
                                            disabled={isProjectUpdating || !position.payout_amount || isEditingStrategy}
                                            onClick={async () => {
                                                await onFormReset()
                                                setIsEditingPayout(false)
                                            }}>
                                        Cancel
                                    </Button>
                                    <Button type="primary"
                                            disabled={isProjectUpdating || !position.payout_amount || isEditingStrategy}
                                            onClick={async () => {
                                                await onFormSubmit({})
                                                setIsEditingPayout(false)
                                            }}>
                                        Save
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex flex-row items-center gap-4">
                                    <Text strong className="text-primary whitespace-nowrap">
                                        ${TB.formatNumber(position.payout_amount, 2)}
                                    </Text>
                                    {!position.hasBeenFilled() && !editingDeliverable && (
                                        <Button type="primary" disabled={isProjectUpdating} onClick={() => setIsEditingPayout(true)}>
                                            Update
                                        </Button>
                                    )}
                                </div>
                            )}
                            {parseFloat(position.estimatedFixedFeeTalentPayoutByBudget()) === parseFloat(position.payout_amount) && (
                                <div className="absolute bottom-[-21px] left-3 right-0">
                                    <div className="flex flex-row items-center gap-1">
                                        <div className="h-2.5 w-2.5 rounded-full" style={{backgroundColor: TB.config('color.info-dark')}}/>
                                        <span className="italic whitespace-nowrap text-xs">
                                            Defaults to client budget minus markup
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex-grow flex flex-row justify-end gap-14 px-12">
                    <div className="flex flex-col gap-1.5 items-start">
                        <p className="text-xs font-semibold whitespace-nowrap">
                            Client Budget
                        </p>
                        <p className="text-danger text-lg whitespace-nowrap">
                            {TB.formatCurrency(position.budget || 10000, false)}
                        </p>
                    </div>
                    {totalHours > 0 && (
                        <div className="flex flex-col gap-1.5 items-start">
                            <p className="text-xs font-semibold whitespace-nowrap">
                                Total Position Hours
                            </p>
                            <p className="text-danger text-lg whitespace-nowrap">
                                {totalHours} hours
                            </p>
                        </div>
                    )}
                    <div className="flex flex-col gap-1.5 items-start">
                        <p className="text-xs font-semibold flex flex-row gap-1.5 items-center whitespace-nowrap">
                            <span>
                                {position.assigned_candidate ? 'Est.' : 'Target'} Talent Payout
                            </span>
                            <InfoTooltip placement="right" type="dark">
                                <span className="text-xs px-2 py-3 text-white">
                                    Talent Payout is based on the&nbsp;
                                    {position.assigned_candidate ?
                                        'selected candidate\'s proposed payout.' :
                                        'sum of each deliverable\'s payout.'
                                    }
                                </span>
                            </InfoTooltip>
                        </p>
                        <p className="text-danger text-lg whitespace-nowrap">
                            {TB.formatCurrency(estimatedTalentPayout, false)}
                        </p>
                    </div>
                    <div className="flex flex-col gap-1.5 items-start">
                        <p className="text-xs font-semibold flex flex-row gap-1.5 items-center whitespace-nowrap">
                            <span>
                                Est. Client Cost
                            </span>
                            <InfoTooltip placement="right" type="dark">
                                <span className="text-xs px-2 py-3 text-white">
                                    Estimated Cost is based on the&nbsp;
                                    {position.assigned_candidate ?
                                        'selected candidate\'s proposed payout.' :
                                        'average Standard Platform Market Rate for each role.'
                                    }
                                    &nbsp;The cost includes
                                    the {position.assigned_candidate ? 'candidate\'s' : 'default project'} markup fee
                                    of {(position.assigned_candidate?.job_candidate?.percent_markup
                                    || position.percent_markup || project.percent_markup) * 100
                                }%.
                                </span>
                            </InfoTooltip>
                        </p>
                        <p className="text-danger text-lg whitespace-nowrap">
                            {TB.formatCurrency(estimatedClientCost, false)}
                        </p>
                    </div>

                </div>
            </div>

            <hr/>

            <div className="min-h-[250px] relative flex flex-col items-stretch">
                {isEditingStrategy && (
                    <div className="flex flex-col items-stretch">
                        <PricingStrategyForm {...props}
                                             onClose={() => setIsEditingStrategy(false)}/>
                    </div>
                )}
                <div className="relative">
                    <div className={TB.mapClass("flex flex-col gap-4", {
                        "pointer-events-none opacity-20": !position.hasFixedFeePricingStrategy() || isEditingStrategy
                    })}>
                        <div className="flex flex-row items-center gap-10">
                            <p className="text-base font-semibold">
                                Scoped Deliverables
                            </p>
                            <Button
                                type="default"

                                size="small" className="text-xs bg-gray-800 text-white border-gray-800 hover:border-black hover:bg-black focus:bg-black focus::border-black disabled:opacity-60" disabled={isDisabled || position.hasBeenFilled()}
                                    onClick={async () => {
                                        let confirmGenerate = await TB.alert('confirm', "Auto-generate Scope?", "Click confirm to use AI to generate the deliverable payouts based on JD, budget, and roles. Note: this will remove any previous changes.", [
                                            {title: "Confirm"},
                                            {title: "Cancel"},
                                        ])
                                        if (confirmGenerate) {
                                            return generateScoping();
                                        }
                                    }}
                            >
                                Use AI to generate scope based on budget
                            </Button>
                        </div>
                        <Table
                            rowKey="id"
                            dataSource={TB.orderBy(position.deliverables, 'id', 'asc')}
                            bordered
                            size="middle"
                            pagination={false}>
                            <Table.Column
                                title="Deliverables"
                                dataIndex="name"
                                fixed="left"
                                width={150}
                                render={name => (
                                    <p className="text-xs">{name}</p>
                                )}
                            />
                            <Table.Column
                                title="Description"
                                dataIndex="description"
                                width={200}
                                render={description => (
                                    <div className="relative">
                                        <div
                                            dangerouslySetInnerHTML={{__html: description || '<i>No Description Provided</i>'}}
                                            className="text-xs line-clamp-3 overflow-ellipsis"/>
                                    </div>
                                )}
                            />
                            <Table.Column
                                title="Target Delivery Date"
                                dataIndex="target_delivery_at"
                                width={110}
                                render={(target_delivery_at, deliverable) => {

                                    return (
                                        (isEditingPayout || editingDeliverable !== deliverable.id) ? (
                                            <div className="relative">
                                                <p className="text-xs">
                                                    {target_delivery_at ? TB.moment(target_delivery_at).format('ll') : (
                                                        <Text type="secondary" className="whitespace-nowrap text-xs italic">No Date Provided</Text>
                                                    )}
                                                </p>
                                            </div>
                                        ) : (
                                            <DatePicker
                                                disabledDate={date => false}
                                                value={target_delivery_at ? TB.moment(target_delivery_at) : undefined}
                                                defaultPickerValue={target_delivery_at ? TB.moment(target_delivery_at) : undefined}
                                                format="ll"
                                                size="small"
                                                onChange={value => onDeliverableChange(deliverable.id, {target_delivery_at: value?.toISOString()})}/>

                                        )
                                    )
                                }}
                            />

                            <Table.Column
                                title={<span className="whitespace-nowrap">Estimated Hours {!position.fixedFeePricingStrategyIsFullBreakdown() && <i>(optional)</i>}</span>}
                                dataIndex="total_hours"
                                width={(TB.find(position.deliverables, del => del.total_hours > 0 )&& !position.fixedFeePricingStrategyIsFullBreakdown()) ? 250 : 40}
                                render={(_, deliverable) => {

                                    return (
                                        <div className="flex flex-row items-center flex-nowrap justify-between gap-3">
                                            {(
                                                (isEditingPayout || editingDeliverable !== deliverable.id) ? (
                                                    <p className={TB.mapClass("text-xs", {"line-through opacity-60 text-danger": !deliverable.is_active})}>
                                                        {(!TB.isNaN(parseInt(deliverable.total_hours)) && parseInt(deliverable.total_hours) > 0) ? (
                                                            <span className="whitespace-nowrap">
                                                                {`${deliverable.total_hours} hours`}
                                                            </span>
                                                        ) : (position.fixedFeePricingStrategyIsFullBreakdown() && deliverable.is_active) ? (
                                                            <span className="text-danger font-semibold">
                                                                Missing!
                                                            </span>
                                                        ) : (
                                                            <Text type="secondary" className="whitespace-nowrap text-xs italic">
                                                                N/A
                                                            </Text>
                                                        )}
                                                    </p>
                                                ) : (
                                                    <InputNumber
                                                        controls={false}
                                                        size="small"
                                                        defaultValue={deliverable.total_hours || ''}
                                                        onChange={value => {
                                                            let params = {total_hours: value};
                                                            if (position.fixedFeePricingStrategyIsFullBreakdown()) {
                                                                params.payout_amount = Number(position.exact_standard_premarkup_rate * (value || 0)).toFixed(2);
                                                            }
                                                            return onDeliverableChange(deliverable.id, params)
                                                        }}
                                                        disabled={isProjectUpdating}
                                                        min={0}
                                                        max={500}
                                                    />
                                                )
                                            )}
                                            {deliverable.total_hours > 0 && !position.fixedFeePricingStrategyIsFullBreakdown() && (
                                                <React.Fragment>
                                                    <div className="flex flex-row gap-4 ml-auto items-center">
                                                        <ArrowRightOutlined className="text-lg text-black"/>
                                                        <div
                                                            className="bg-amber-100 border border-amber-300 rounded-md px-1 py-1 flex flex-row items-center gap-1">
                                                            <div className="flex flex-col pr-2">
                                                                <p className="text-xxs font-medium text-black max-w-36">
                                                                Est. Deliverable Payout
                                                            </p>

                                                                <p className="text-xxs font-medium text-amber-600">
                                                                    ${TB.formatNumber(position.exact_standard_premarkup_rate)}/hr x {deliverable.total_hours} hours
                                                                    {deliverable.total_hours && (
                                                                        <InfoTooltip iconColor={'rgba(0,0,0)'} iconSize={9}
                                                                                     iconStyle={{marginTop: -2, marginLeft: 3}} type="dark">
                                                                            <p className="m-0 px-1 py-1.5 text-white">
                                                                                This is just a calculation to help determine scope. It is based on the avg. standard platform rate for the Wripple Roles selected
                                                                            </p>
                                                                        </InfoTooltip>
                                                                    )}
                                                                </p>
                                                            </div>

                                                            <span className="w-1 bg-black h-4/5"/>

                                                            <p className="font-medium text-sm text-amber-600 whitespace-nowrap">
                                                                {deliverable.total_hours ? `$${TB.formatNumber((position.exact_standard_premarkup_rate * (deliverable.total_hours || 0)))}` : '-'}
                                                            </p>

                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            )}

                                        </div>
                                    )
                                }}
                            />

                            {/*<Table.Column*/}
                            {/*    title="Market Rate"*/}
                            {/*    dataIndex="platform_rate"*/}
                            {/*    width={75}*/}
                            {/*    render={(platform_rate, deliverable) => (*/}
                            {/*        <p className={TB.mapClass("text-xs", {"line-through opacity-60 text-danger": !deliverable.is_active})}>*/}
                            {/*            {TB.formatCurrency(position.platform_rate)}/hr*/}
                            {/*        </p>*/}
                            {/*    )}*/}
                            {/*/>*/}
                            {!position.fixedFeePricingStrategyIsTotalPayout() && (
                                <React.Fragment>
                                    <Table.Column
                                        title={<span className="whitespace-nowrap">Target Talent Payout</span>}
                                        dataIndex="payout_amount"
                                        width={75}
                                        render={(_, deliverable) => (
                                            (
                                                (isEditingPayout || editingDeliverable !== deliverable.id || position.fixedFeePricingStrategyIsFullBreakdown()) ? (
                                                    <p className={TB.mapClass("text-xs", {"line-through opacity-60 text-danger": !deliverable.is_active})}>
                                                        {position.fixedFeePricingStrategyIsFullBreakdown() ? (
                                                            <span>
                                                                {deliverable.total_hours ? `$${TB.formatNumber(position.exact_standard_premarkup_rate * (deliverable.total_hours || 0))}` : '-'}

                                                                {deliverable.total_hours > 0 && (
                                                                    <InfoTooltip iconColor={'rgba(0,0,0,0.75)'} iconSize={11}
                                                                                 iconStyle={{marginTop: -2, marginLeft: 5}} type="dark">
                                                                        <p className="m-0 px-1 py-1.5 text-white whitespace-nowrap">
                                                                            {deliverable.total_hours} hours × Talent Market Rate
                                                                            ${position.exact_standard_premarkup_rate}/hr
                                                                        </p>
                                                                    </InfoTooltip>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                {deliverable.payout_amount ? `$${TB.formatNumber(deliverable.payout_amount)}` : (
                                                                    (position.fixedFeePricingStrategyIsPayoutByDeliverable() && deliverable.is_active) ? (
                                                                        <span className="text-danger font-semibold">
                                                                            Missing!
                                                                        </span>
                                                                    ) : '-'
                                                                )}
                                                            </span>
                                                        )}

                                                    </p>
                                                ) : (
                                                    <InputNumber
                                                        controls={false}
                                                        min={1}
                                                        addonBefore="$"
                                                        precision={2}
                                                        size="small"
                                                        defaultValue={deliverable.payout_amount || 0}
                                                        onChange={value => onDeliverableChange(deliverable.id, {payout_amount: value})}
                                                        disabled={isProjectUpdating}
                                                    />
                                                )
                                            )
                                        )}
                                    />
                                    <Table.Column
                                        title={<span className="whitespace-nowrap">Est Client Cost</span>}
                                        dataIndex="estimated_cost"
                                        width={75}
                                        render={(_, deliverable) => {
                                            if (position.fixedFeePricingStrategyIsFullBreakdown()) {
                                                return (
                                                    <p className={TB.mapClass("text-xs", {"line-through opacity-60 text-danger": !deliverable.is_active})}>
                                                        {deliverable.total_hours ? `$${TB.formatNumber((deliverable.total_hours * position.platform_rate))}` : '-'}
                                                    </p>
                                                )
                                            }
                                            return (
                                                <p className={TB.mapClass("text-xs", {"line-through opacity-60 text-danger": !deliverable.is_active})}>
                                                    {deliverable.payout_amount ? `$${TB.formatNumber((deliverable.payout_amount * (1 + (project.percent_markup || 0))))}` : '-'}
                                                </p>
                                            )
                                        }}
                                    />
                                </React.Fragment>
                            )}
                            <Table.Column
                                title="Active?"
                                dataIndex="is_active"
                                width={40}
                                render={(_, deliverable) => (
                                    <div>
                                        <Switch
                                            size="small"
                                            checkedChildren={<CheckOutlined/>}
                                            unCheckedChildren={<CloseOutlined/>}
                                            disabled={isEditingPayout || position.hasBeenFilled() || !!editingDeliverable || isProjectUpdating || isDisabled}
                                            checked={deliverable.is_active}
                                            onChange={async (checked) => {
                                                console.log('onchange', checked);

                                                let positions = await onDeliverableChange(deliverable.id, {is_active: checked})
                                                return onProjectFormSubmit({positions});
                                            }}
                                        />
                                    </div>
                                )}
                            />
                            <Table.Column
                                title="Actions"
                                dataIndex="operation"
                                width={100}
                                render={(_, deliverable) => (
                                    (isEditingPayout || editingDeliverable !== deliverable.id) ? (
                                        <button className="btn btn-link btn-link-primary"
                                                disabled={position.hasBeenFilled() || (editingDeliverable && editingDeliverable !== deliverable.id)}
                                                onClick={() => setEditingDeliverable(deliverable.id)}>
                                            Edit
                                        </button>
                                    ) : (
                                        <div className="flex flex-row gap-1 items-center">
                                            <button className="btn btn-link" disabled={isProjectUpdating}
                                                    onClick={async () => {
                                                        await onFormReset();
                                                        return setEditingDeliverable(false)
                                                    }}>
                                                Cancel
                                            </button>
                                            <button className="btn btn-link btn-link-primary"
                                                    disabled={isProjectUpdating}
                                                    onClick={async () => {
                                                        await onFormSubmit({});
                                                        return setEditingDeliverable(false)
                                                    }}>
                                                Update
                                            </button>
                                        </div>
                                    )
                                )}
                            />
                        </Table>
                    </div>

                    {!position.hasFixedFeePricingStrategy() && (
                        <div
                            className="absolute left-0 right-0 top-0 bottom-0 justify-center mx-auto flex flex-col items-center gap-6">
                            <Card className="inline-flex">
                                <p className="text-medium text-base text-danger">
                                    Proposal Pricing Strategy Required
                                </p>
                                <p className="text-sm mt-1">
                                    You must select an option for proposal strategy before you can scope the role and
                                    its deliverables
                                </p>
                                {!editingDeliverable && !isEditingPayout && !isEditingStrategy && (
                                    <Button className="ml-auto self-end mt-4" type="primary"
                                            onClick={() => setIsEditingStrategy(true)}>
                                        Update
                                    </Button>
                                )}
                            </Card>
                        </div>
                    )}
                </div>


            </div>
            <hr/>

            <div className={TB.mapClass("flex flex-col gap-4", {
                "pointer-events-none opacity-20": !position.hasFixedFeePricingStrategy()
            })}>
                <div className="flex flex-row items-center justify-between">
                    <p className="text-base font-semibold">
                        Candidate Scopes
                    </p>
                    <div className="flex flex-row items-center gap-2">
                        <Switch
                            size="small"
                            className={position.is_talent_payout_auto_confirmed ? 'bg-green-400' : ''}
                            checkedChildren={<CheckOutlined/>}
                            // unCheckedChildren={<CloseOutlined/>}
                            checked={position.is_talent_payout_auto_confirmed}
                            disabled={isDisabled}
                            onChange={async (checked) => {
                                console.log('ischeck', checked)
                                onFormSubmit({is_talent_payout_auto_confirmed: checked})
                            }}
                        />
                        Auto Confirm Talent payouts after they submit proposal
                    </div>
                </div>
                <PositionCandidatesTable position={position}/>
            </div>
        </div>
    );
};

export default PositionDeliverablesTable;